import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { isDemoOrg } from "@constants/branding";
import { useState, useEffect, useRef } from "react";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { organizationsApi } from "@services/api/organizations";
import DropDown from "@components/ui/dropdown/DropDown";
import { OrgDecennale } from "types/org";

export const Company = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);
  const fetchOrgData = useDataStore((state) => state.fetchOrgData);
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);

  // Local state for org form
  const [orgName, setOrgName] = useState(orgData?.infos.name || "");
  const [orgEmail, setOrgEmail] = useState(orgData?.infos.email || "");
  const [orgPhoneNumber, setOrgPhoneNumber] = useState(
    orgData?.infos.phone_number || "",
  );
  const [orgSiret, setOrgSiret] = useState(orgData?.infos.siret || "");
  const [orgCity, setOrgCity] = useState(orgData?.infos.city || "");
  const [orgPostal, setOrgPostal] = useState(orgData?.infos.postal || "");
  const [orgAddress, setOrgAddress] = useState(orgData?.infos.address || "");

  // Décennales state
  const [decennales, setDecennales] = useState<OrgDecennale[]>(
    orgData?.config?.decennales || [],
  );
  const [newDecStartDate, setNewDecStartDate] = useState("");
  const [newDecEndDate, setNewDecEndDate] = useState("");
  const [newDecDocId, setNewDecDocId] = useState("");
  const [isSavingDecennales, setIsSavingDecennales] = useState(false);

  // Sync local state when orgData loads or changes externally
  useEffect(() => {
    if (orgData) {
      setOrgName(orgData.infos.name || "");
      setOrgEmail(orgData.infos.email || "");
      setOrgPhoneNumber(orgData.infos.phone_number || "");
      setOrgSiret(orgData.infos.siret || "");
      setOrgCity(orgData.infos.city || "");
      setOrgPostal(orgData.infos.postal || "");
      setOrgAddress(orgData.infos.address || "");
      setDecennales(orgData.config?.decennales || []);
    }
  }, [orgData?.id]);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !orgData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({
        type: "error",
        message: "Format invalide. Acceptés : JPG, PNG, WEBP",
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification({ type: "error", message: "La taille dépasse 5MB" });
      return;
    }

    setIsUploadingLogo(true);
    try {
      await organizationsApi.uploadLogo(orgData.id, file);
      await fetchOrgData(orgData.id);
      showNotification({
        type: "success",
        message: "Logo enregistré avec succès",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Échec du téléchargement du logo",
      });
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = "";
    }
  };

  const handleSaveOrg = async () => {
    if (!orgData?.id) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateOrgData(orgData.id, {
        infos: {
          name: orgName,
          email: orgEmail,
          phone_number: orgPhoneNumber,
          siret: orgSiret,
          city: orgCity,
          postal: orgPostal,
          address: orgAddress,
        },
      });
      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
    } catch (error: any) {
      console.error("Failed to update org:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDecennale = () => {
    if (!newDecDocId || !newDecStartDate || !newDecEndDate) {
      showNotification({ type: "error", message: "Veuillez remplir tous les champs" });
      return;
    }
    if (newDecStartDate >= newDecEndDate) {
      showNotification({ type: "error", message: "La date de fin doit être postérieure à la date de début" });
      return;
    }
    setDecennales((prev) => [
      ...prev,
      { library_document_id: newDecDocId, start_date: newDecStartDate, end_date: newDecEndDate },
    ]);
    setNewDecDocId("");
    setNewDecStartDate("");
    setNewDecEndDate("");
  };

  const handleRemoveDecennale = (index: number) => {
    setDecennales((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveDecennales = async () => {
    if (!orgData?.id) return;
    setIsSavingDecennales(true);
    try {
      await updateOrgData(orgData.id, {
        config: { decennales },
      });
      showNotification({ type: "success", message: "Décennales enregistrées" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'enregistrement" });
    } finally {
      setIsSavingDecennales(false);
    }
  };

  const getDocName = (docId: string) => {
    const doc = libraryDocuments.find((d) => d.id === docId);
    return doc?.name || docId;
  };

  const disabled = !isOwnerOrAdmin(userData);

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Entreprise</Text1>
          <Text2>Configurez votre entreprise ici.</Text2>
          <Text3>
            Gérez vos informations d'entreprise, vos préférences et les
            paramètres associés.
          </Text3>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Logo de l'entreprise</div>
          {orgData?.infos.logo_url ? (
            <img
              src={orgData.infos.logo_url}
              className="w-24 h-24 rounded-xl border object-contain flex-shrink-0"
            ></img>
          ) : isDemoOrg(orgData?.id) ? (
            <div className="w-24 h-24 rounded-xl border bg-neutral-100 flex-shrink-0" />
          ) : (
            <img
              src={require("@assets/photos/mbmo.png")}
              className="w-24 h-24 rounded-xl border object-contain flex-shrink-0"
            ></img>
          )}
          <input
            ref={logoFileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18}></Icon1>}
            label={
              isUploadingLogo
                ? "Téléchargement..."
                : orgData?.infos.logo_url
                  ? "Changer le logo"
                  : "Ajouter une image"
            }
            variant="large"
            disabled={isUploadingLogo || disabled}
            onClick={() => logoFileInputRef.current?.click()}
          ></SecondaryButton>
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="ID"
            value={orgData?.id || ""}
            placeholder=""
            disabled={true}
          />{" "}
          <TextField
            label="Nom affiché"
            value={orgName}
            onChange={(value) => setOrgName(value)}
            placeholder="Dupont"
            disabled={disabled}
          />
          <TextField
            label="Adresse e-mail de contact"
            placeholder="contact@life.com"
            value={orgEmail}
            onChange={(value) => setOrgEmail(value)}
            disabled={disabled}
          />
          <TextField
            label="Numéro de téléphone de contact"
            placeholder="06 12 34 56 78"
            type="phone"
            value={orgPhoneNumber}
            onChange={(value) => setOrgPhoneNumber(value)}
            disabled={disabled}
          />
          <TextField
            label="SIRET"
            placeholder="123 456 789 00010"
            value={orgSiret}
            onChange={(value) => setOrgSiret(value)}
            disabled={disabled}
          />
          <TextField
            label="Ville"
            placeholder="Rennes"
            value={orgCity}
            onChange={(value) => setOrgCity(value)}
            disabled={disabled}
          />
          <TextField
            label="Code postal"
            placeholder="35000"
            value={orgPostal}
            onChange={(value) => setOrgPostal(value)}
            disabled={disabled}
          />
          <TextField
            label="Adresse"
            placeholder="1 rue des fleurs"
            value={orgAddress}
            onChange={(value) => setOrgAddress(value)}
            disabled={disabled}
          />
          <TextField
            label="Adresse e-mail d'envoi"
            placeholder="noreply@entreprise.com"
            value={orgData?.infos.sender_email || ""}
            disabled={true}
          />
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={
              isSaving ? "Enregistrement..." : "Enregistrer les modifications"
            }
            onClick={handleSaveOrg}
            disabled={isSaving || disabled}
          ></Button>
        </div>

        {/* Décennales entreprise */}
        <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
          <div className="text-h2 font-semibold text-neutral-700">
            Décennales entreprise
          </div>
          <div className="text-h3 text-neutral-400">
            Associez des documents de la bibliothèque comme décennales de votre entreprise (Maître d'Oeuvre).
          </div>

          {/* Existing décennales */}
          {decennales.length > 0 && (
            <div className="flex flex-col gap-2">
              {decennales.map((dec, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center gap-3 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200"
                >
                  <Icon1 name="pdf" size={20} />
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="text-h3 font-medium truncate">
                      {getDocName(dec.library_document_id)}
                    </div>
                    <div className="text-h4 text-neutral-400">
                      {dec.start_date} — {dec.end_date}
                    </div>
                  </div>
                  {!disabled && (
                    <SecondaryButton
                      variant="small"
                      danger
                      onClick={() => handleRemoveDecennale(index)}
                    >
                      Retirer
                    </SecondaryButton>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add new décennale */}
          {!disabled && (
            <div className="flex flex-col gap-2 border border-dashed border-neutral-300 rounded-lg p-3">
              <DropDown
                label="Document de la bibliothèque"
                placeholder="Sélectionner un document..."
                options={libraryDocuments.map((doc) => ({
                  value: doc.id,
                  label: doc.name,
                }))}
                value={newDecDocId}
                onSelect={(value) => setNewDecDocId(value as string)}
              />
              <div className="flex flex-row gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-h4 text-neutral-500 font-medium">Date de début</label>
                  <input
                    type="date"
                    value={newDecStartDate}
                    onChange={(e) => setNewDecStartDate(e.target.value)}
                    className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-h4 text-neutral-500 font-medium">Date de fin</label>
                  <input
                    type="date"
                    value={newDecEndDate}
                    onChange={(e) => setNewDecEndDate(e.target.value)}
                    className="border border-neutral-300 rounded-lg px-3 py-2 text-h3"
                  />
                </div>
              </div>
              <SecondaryButton
                startIcon={<Icon1 name="plus" size={16} />}
                label="Ajouter cette décennale"
                variant="large"
                onClick={handleAddDecennale}
              />
            </div>
          )}

          <Button
            className="mt-2"
            label={isSavingDecennales ? "Enregistrement..." : "Enregistrer les décennales"}
            onClick={handleSaveDecennales}
            disabled={isSavingDecennales || disabled}
          />
        </div>
      </div>
    </div>
  );
};
